/**
 * Editing a project file in the window (RFC 0277 3f, S7c).
 *
 * Two pieces, both kept out of React so they can be tested:
 *
 * - `draftHandle`: a local Automerge document shaped like a DocumentDoc, that
 *   never enters the repo or syncs. The Code app edits a document through
 *   `change`, so a throwaway one lets it edit a file without a second editor.
 * - `createAutosave`: VS Code with Auto Save on. A write a second after typing
 *   stops, or at once on save(), each carrying the hash of what was last read
 *   so a file an agent changed underneath is refused rather than overwritten.
 *   A refusal stops autosaving until the person resolves it.
 */

import * as A from '@automerge/automerge'
import type { DocHandle } from '@automerge/automerge-repo'
import type { DocumentDoc } from '@mod/core/model'

/** `readAt` for a file that did not exist when it was read. */
export const ABSENT = 'absent'

/** sha256 of the UTF-8 bytes, hex: the hash the git server compares. */
export async function contentHash(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('')
}

type Draft = Pick<DocumentDoc, 'path' | 'mime' | 'content'>

/** A local, unsynced document the Code app can edit, with `on('change')`. */
export function draftHandle(
  id: string,
  initial: Draft,
): DocHandle<DocumentDoc> & { text(): string } {
  let doc = A.from({
    id,
    workspaceId: '',
    createdAt: 0,
    updatedAt: 0,
    ...initial,
  } as unknown as Record<string, unknown>) as unknown as A.Doc<DocumentDoc>
  const listeners = new Set<() => void>()
  return {
    documentId: id,
    doc: () => doc,
    change: (fn: A.ChangeFn<DocumentDoc>) => {
      doc = A.change(doc, fn)
      for (const l of listeners) l()
    },
    on: (_event: string, fn: () => void) => listeners.add(fn),
    off: (_event: string, fn: () => void) => listeners.delete(fn),
    whenReady: async () => {},
    text: () => String(doc.content ?? ''),
  } as unknown as DocHandle<DocumentDoc> & { text(): string }
}

export type SaveAnswer =
  | { ok: true; tracked: boolean; hash: string }
  | {
      ok: false
      reason: 'changed-on-disk'
      theirs: string | null
      hash: string
    }
  | { ok: false; reason: string }

export type AutosaveState =
  | { kind: 'saved'; tracked: boolean | null }
  | { kind: 'edited' }
  | { kind: 'saving' }
  | { kind: 'refused'; theirs: string | null }
  | { kind: 'failed'; message: string }

export interface Autosave {
  /** The draft changed; write it after the pause, unless refused. */
  edited(): void
  /** Write now (⌘S). */
  save(): Promise<void>
  /** Take what is on disk now as read, e.g. after "Discard mine". */
  reread(text: string | null): Promise<void>
  state(): AutosaveState
  dispose(): void
}

export function createAutosave(opts: {
  /** The draft's current text. */
  text: () => string
  /** What was read, or null when the file did not exist. */
  read: string | null
  /** The server call. */
  write: (content: string, readAt: string) => Promise<SaveAnswer>
  onState: (s: AutosaveState) => void
  delayMs?: number
}): Autosave {
  const delay = opts.delayMs ?? 1000
  let readAt: Promise<string> =
    opts.read === null ? Promise.resolve(ABSENT) : contentHash(opts.read)
  let lastSaved: string | null = opts.read
  let current: AutosaveState = { kind: 'saved', tracked: null }
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight: Promise<void> | null = null
  let disposed = false

  const set = (s: AutosaveState) => {
    current = s
    if (!disposed) opts.onState(s)
  }
  const blocked = () => current.kind === 'refused'

  const run = async (): Promise<void> => {
    if (timer) clearTimeout(timer)
    timer = null
    if (blocked()) return
    // One write at a time: the next one needs the hash this one returns.
    if (inFlight) await inFlight
    const content = opts.text()
    if (content === lastSaved) {
      if (current.kind === 'edited') set({ kind: 'saved', tracked: null })
      return
    }
    set({ kind: 'saving' })
    inFlight = (async () => {
      try {
        const answer = await opts.write(content, await readAt)
        if (answer.ok) {
          readAt = Promise.resolve(answer.hash)
          lastSaved = content
          // Typed again while the write was in flight: still edited.
          if (opts.text() !== content) {
            set({ kind: 'edited' })
            timer = setTimeout(() => void run(), delay)
          } else set({ kind: 'saved', tracked: answer.tracked })
        } else if (answer.reason === 'changed-on-disk') {
          set({
            kind: 'refused',
            theirs: 'theirs' in answer ? answer.theirs : null,
          })
        } else set({ kind: 'failed', message: answer.reason })
      } catch (e) {
        set({
          kind: 'failed',
          message: e instanceof Error ? e.message : String(e),
        })
      }
    })()
    await inFlight
    inFlight = null
  }

  return {
    edited() {
      if (blocked()) return
      if (current.kind !== 'saving') set({ kind: 'edited' })
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void run(), delay)
    },
    save: run,
    async reread(text) {
      if (timer) clearTimeout(timer)
      timer = null
      readAt = text === null ? Promise.resolve(ABSENT) : contentHash(text)
      await readAt
      lastSaved = text
      set({ kind: 'saved', tracked: null })
    },
    state: () => current,
    dispose() {
      disposed = true
      if (timer) clearTimeout(timer)
    },
  }
}
